import { useCallback, useState, useRef, useEffect } from 'react';

interface UseVirtualScrollOptions {
  itemCount: number;
  itemHeight: number;
  containerHeight: number;
  overscan?: number;
}

interface VirtualScrollResult {
  virtualItems: Array<{ index: number; offsetTop: number }>;
  totalHeight: number;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

/**
 * Simple virtual scrolling hook that only renders visible items in the DOM.
 * Items outside the viewport are removed from the DOM to improve performance.
 */
export const useVirtualScroll = ({
  itemCount,
  itemHeight,
  containerHeight,
  overscan = 3,
}: UseVirtualScrollOptions): VirtualScrollResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  // Calculate visible range
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(itemCount, Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan);

  // Generate virtual items
  const virtualItems = [];
  for (let i = startIndex; i < endIndex; i++) {
    virtualItems.push({
      index: i,
      offsetTop: i * itemHeight,
    });
  }

  const totalHeight = itemCount * itemHeight;

  // Reset scroll when item count changes significantly
  useEffect(() => {
    if (scrollRef.current && scrollTop > totalHeight) {
      scrollRef.current.scrollTop = 0;
      setScrollTop(0);
    }
  }, [totalHeight, scrollTop]);

  return {
    virtualItems,
    totalHeight,
    scrollRef,
    onScroll,
  };
};
